import { mapActivitiesToCalendarEvents } from '@paintscout/util/builder/util/map-activities-to-calendar-events'
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import {
  useSaveEventMutation,
  useSearchActivitiesQuery,
  useUnsheduledJobsQuery,
  useSearchUsersQuery
} from '@paintscout/api'
import type { User, EventActivity } from '@paintscout/api'
import type { CalendarEvent } from 'paintscout'
import type { View } from 'react-big-calendar'
import { useLocalStorage } from 'shared/hooks'
import { jsonToLucene } from 'json-to-lucene'
import type { Filter, ValueFilter } from 'json-to-lucene'
import { useSnackbar } from 'notistack'
import { useClientOptions, useUser } from '@paintscout/ui-business'
import { handlePipelineQuery } from 'src/components/pages/deals/DealList/ListView'
import { addAuth0Prefix, removeAuth0Prefix } from '@paintscout/util'
import { getCrews } from '@paintscout/util/builder/options/get-crews'
import { Sentry } from 'src/util/appSentry'
import { CALENDAR_BUFFER_DAYS, getViewRange } from '../Calendar/calendarUtils'
import type { ZoomLevel } from './TimelineHeader'
import { filterScheduledEvents } from './scheduledEventFilters'

interface ProductionCalendarContextValue {
  events: CalendarEvent[]
  setEvents: (events: CalendarEvent[]) => void
  visibleEvents: CalendarEvent[]
  unscheduledEvents: CalendarEvent[]
  totalUnscheduledEvents: number
  fetchMoreUnscheduledEvents: () => Promise<any>
  scheduledFilters: {
    scheduledSelectedProjectTypes: string[] | null
    setScheduledSelectedProjectTypes: (types: string[] | null) => void
    scheduledSelectedProjectManagers: string[]
    setScheduledSelectedProjectManagers: (managers: string[]) => void
    scheduledSelectedSalesReps: string[]
    setScheduledSelectedSalesReps: (salesReps: string[]) => void
    scheduledMinPrice: number | null
    scheduledMaxPrice: number | null
    setScheduledMinPrice: (price: string) => void
    setScheduledMaxPrice: (price: string) => void
  }
  unscheduledFilters: {
    unscheduledSelectedProjectTypes: string[] | null
    setUnscheduledSelectedProjectTypes: (types: string[] | null) => void
    unscheduledMinPrice: number | null
    unscheduledMaxPrice: number | null
    setUnscheduledMinPrice: (price: string) => void
    setUnscheduledMaxPrice: (price: string) => void
    unscheduledMinHours: number | null
    unscheduledMaxHours: number | null
    setUnscheduledMinHours: (hours: string) => void
    setUnscheduledMaxHours: (hours: string) => void
    unscheduledSelectedProjectManagers: string[]
    setUnscheduledSelectedProjectManagers: (stages: string[]) => void
    unscheduledSelectedStages: string[]
    setUnscheduledSelectedStages: (stages: string[]) => void
    unscheduledTags: string[]
    setUnscheduledTags: (tags: string[]) => void
  }
  date: Date
  setDate: (date: Date) => void
  targetDate: Date
  setTargetDate: (date: Date) => void
  startDate: Date
  endDate: Date
  setVisibleWindow: (targetDate: Date) => void
  zoomLevel: ZoomLevel
  setZoomLevel: (zoomLevel: ZoomLevel) => void
  monthColumns: number
  setMonthColumns: (count: number) => void
  quarterColumns: number
  setQuarterColumns: (count: number) => void
  view: View
  setView: (view: View) => void
  tab: 'calendar' | 'map'
  loading: boolean
  selectedUsers?: User[]
  setSelectedUsers?: (users: User[]) => void

  updateEvent: (event: CalendarEvent) => void
  deleteEvent: (event: CalendarEvent) => void
}

const ProductionCalendarContext = createContext<ProductionCalendarContextValue | undefined>(undefined)

export const ProductionCalendarProvider: React.FC<{ children: React.ReactNode; tab?: 'calendar' | 'map' }> = ({
  children,
  tab = 'calendar'
}) => {
  const { enqueueSnackbar } = useSnackbar()
  const { isSuperadmin } = useUser()
  const { clientId, options } = useClientOptions()
  const crews = getCrews({ options, includeTrashed: true })
  const [view, setView] = useLocalStorage<View>('productionCalendarView', 'agenda')
  const [saveEvent] = useSaveEventMutation()
  const [loading, setLoading] = useState(true)
  const [date, setDate] = useState<Date>(new Date())
  const [events, setEvents] = useState<CalendarEvent[]>([])
  const eventsRef = useRef<CalendarEvent[]>([])
  const [selectedUsers, setSelectedUsers] = useState<User[]>([])
  const [unscheduledEvents, setUnscheduledEvents] = useState<CalendarEvent[]>([])
  const unscheduledEventsRef = useRef<CalendarEvent[]>([])
  const [totalUnscheduledEvents, setTotalUnscheduledEvents] = useState<number>(0)
  const usersQuery = isSuperadmin ? `app_metadata.companyId:"${clientId}" AND app_metadata.active:true` : ''

  // -- Scheduled Events Query and State -- //
  const baseFilters: Filter[] = [
    {
      id: 'details.activityType',
      type: 'equal',
      value: 'event'
    } as Filter,
    {
      id: 'details.production',
      type: 'equal',
      value: true
    } as Filter,
    {
      id: 'trashed',
      type: 'equal',
      value: true,
      not: true
    } as Filter,
    {
      id: 'details.canceled',
      type: 'equal',
      value: true,
      not: true
    } as Filter
  ]

  // Single source of truth for the target date - now as state
  // Initialize with today minus 2 days so today appears as the 3rd visible day
  const [targetDate, setTargetDate] = useState(() => {
    const today = new Date(Date.now())
    today.setDate(today.getDate() - 2) // Start 2 days before today
    return today
  })

  // Calculate timeline range based on zoom level
  // const startDate = new Date(targetDate)
  // startDate.setHours(0, 0, 0, 0) // Normalize to midnight local time
  // const endDate = (() => {
  //   const d = new Date(startDate)
  //   d.setDate(d.getDate() + 29)
  //   return d
  // })()

  const BUFFER_DAYS = 30 // or whatever makes sense for your use case

  const [visibleStartDate, setVisibleStartDate] = useState<Date>(() => {
    const date = new Date(targetDate)
    date.setHours(0, 0, 0, 0)
    return date
  })
  const [visibleEndDate, setVisibleEndDate] = useState<Date>(() => {
    const d = new Date(visibleStartDate)
    d.setDate(d.getDate() + 29)
    return d
  })
  const [fetchedStartDate, setFetchedStartDate] = useState<Date>(
    new Date(visibleStartDate.getTime() - BUFFER_DAYS * 86400000)
  )
  const [fetchedEndDate, setFetchedEndDate] = useState<Date>(
    new Date(visibleEndDate.getTime() + BUFFER_DAYS * 86400000)
  )

  const [scheduledSelectedProjectManagers, setScheduledSelectedProjectManagers] = useLocalStorage<string[]>(
    'productionCalendarScheduledProjectManagers',
    []
  )
  const [scheduledSelectedProjectTypes, setScheduledSelectedProjectTypes] = useLocalStorage<string[]>(
    'productionCalendarScheduledProjectTypes',
    []
  )
  const [scheduledSelectedSalesReps, setScheduledSelectedSalesReps] = useLocalStorage<string[]>(
    'productionCalendarScheduledSalesReps',
    []
  )
  const [scheduledMinPrice, setScheduledMinPrice] = useLocalStorage<string>('productionCalendarScheduledMinPrice', '')
  const [scheduledMaxPrice, setScheduledMaxPrice] = useLocalStorage<string>('productionCalendarScheduledMaxPrice', '')
  const [zoomLevel, setZoomLevel] = useState<ZoomLevel>('days')
  const [monthColumns, setMonthColumns] = useState(12)
  const [quarterColumns, setQuarterColumns] = useState(20)
  const filters = [
    ...baseFilters,
    ...(scheduledSelectedSalesReps && scheduledSelectedSalesReps.length
      ? [
          {
            id: 'details.owner.id',
            type: 'in',
            // both auth0 and non-auth0 ids
            value: scheduledSelectedSalesReps.flatMap((id) => [addAuth0Prefix(id), removeAuth0Prefix(id)])
          } as Filter
        ]
      : []),
    ...(scheduledMinPrice
      ? [
          {
            id: 'details.deal.total',
            type: 'greaterThan',
            value: parseFloat(scheduledMinPrice)
          } as Filter
        ]
      : []),
    ...(scheduledMaxPrice
      ? [
          {
            id: 'details.deal.total',
            type: 'lessThan',
            value: parseFloat(scheduledMaxPrice)
          } as Filter
        ]
      : []),
    {
      id: 'details.end',
      type: 'greaterThan',
      value: fetchedStartDate.getTime()
    } as Filter,
    {
      id: 'details.start',
      type: 'lessThan',
      value: fetchedEndDate.getTime()
    } as Filter
  ]

  const rangeFromZoomLevel = (zoomLevel: ZoomLevel, date: Date): Date => {
    const newDate = new Date(date)
    switch (zoomLevel) {
      case 'days':
        // newDate.setFullYear(date.getFullYear(), date.getMonth(), date.getDate())
        newDate.setDate(newDate.getDate() + 29)
        break
      case 'weeks':
        // newDate.setFullYear(date.getFullYear(), date.getMonth(), date.getDate())
        newDate.setDate(date.getDate() + 28 * 7 - 1) // 28 weeks
        break
      case 'months':
        newDate.setMonth(date.getMonth() + Math.max(1, monthColumns) - 1)
        break
      case 'quarters':
        newDate.setMonth(date.getMonth() + Math.max(1, quarterColumns) * 3 - 3)
        break
    }
    return newDate
  }

  // Function to update the visible window and fetch if needed
  const setVisibleWindow = (targetDate: Date) => {
    const newStart = new Date(targetDate)
    newStart.setHours(0, 0, 0, 0)
    const newEnd = rangeFromZoomLevel(zoomLevel, newStart)
    setTargetDate(targetDate)
    setVisibleStartDate(newStart)
    setVisibleEndDate(newEnd)
    // If the new window is outside the fetched buffer, expand and fetch
    if (newStart < fetchedStartDate || newEnd > fetchedEndDate) {
      const expandedStart = new Date(newStart.getTime() - BUFFER_DAYS * 86400000)
      const expandedEnd = new Date(newEnd.getTime() + BUFFER_DAYS * 86400000)
      setFetchedStartDate(expandedStart)
      setFetchedEndDate(expandedEnd)
    }
  }

  useEffect(() => {
    const newStart = new Date(targetDate)
    newStart.setHours(0, 0, 0, 0)
    const newEnd = rangeFromZoomLevel(zoomLevel, newStart)
    setVisibleStartDate(newStart)
    setVisibleEndDate(newEnd)

    if (newStart < fetchedStartDate || newEnd > fetchedEndDate) {
      const expandedStart = new Date(newStart.getTime() - BUFFER_DAYS * 86400000)
      const expandedEnd = new Date(newEnd.getTime() + BUFFER_DAYS * 86400000)
      setFetchedStartDate(expandedStart)
      setFetchedEndDate(expandedEnd)
    }
  }, [zoomLevel, monthColumns, quarterColumns, targetDate])

  // For day/week/month calendar views, expand the fetch window when the user navigates.
  // Agenda view is handled separately via setVisibleWindow / targetDate.
  // fetchedStartDate/fetchedEndDate are in deps so the check uses the latest window values;
  // expansion is idempotent so this doesn't cause infinite loops.
  useEffect(() => {
    if (view === 'agenda') return
    const { start, end } = getViewRange(date, view)
    if (start.getTime() < fetchedStartDate.getTime() || end.getTime() > fetchedEndDate.getTime()) {
      setFetchedStartDate(new Date(start.getTime() - CALENDAR_BUFFER_DAYS * 86400000))
      setFetchedEndDate(new Date(end.getTime() + CALENDAR_BUFFER_DAYS * 86400000))
    }
  }, [date, view, fetchedStartDate, fetchedEndDate])

  const { loading: loadingJobs } = useSearchActivitiesQuery({
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: false,
    variables: {
      query: jsonToLucene({ filters }),
      limit: null
    },
    onCompleted: (data) => {
      // Transform data.activities into CalendarEvent format
      const transformedEvents = mapActivitiesToCalendarEvents(
        data.searchActivities.activities as EventActivity[],
        selectedUsers,
        false
      )
      setEvents(transformedEvents)
    },
    onError: useCallback((error) => {
      console.error(error)
      enqueueSnackbar('Something went wrong, please try again later.', {
        variant: 'error',
        autoHideDuration: 3000
      })
      Sentry.captureException(error)
    }, [])
  })

  // -- Unscheduled Events Query and State -- //
  const [unscheduledSelectedProjectManagers, setUnscheduledSelectedProjectManagers] = useLocalStorage<string[]>(
    'productionCalendarUnscheduledProjectManagers',
    []
  )
  const [unscheduledSelectedProjectTypes, setUnscheduledSelectedProjectTypes] = useLocalStorage<string[]>(
    'productionCalendarUnscheduledProjectTypes',
    []
  )
  const [unscheduledSelectedStages, setUnscheduledSelectedStages] = useLocalStorage<string[]>(
    'productionCalendarUnscheduledStages',
    []
  )
  const [unscheduledTags, setUnscheduledTags] = useLocalStorage<string[]>('productionCalendarUnscheduledTags', [])
  const [unscheduledMinPrice, setUnscheduledMinPrice] = useLocalStorage<string>(
    'productionCalendarUnscheduledMinPrice',
    ''
  )
  const [unscheduledMaxPrice, setUnscheduledMaxPrice] = useLocalStorage<string>(
    'productionCalendarUnscheduledMaxPrice',
    ''
  )
  const [unscheduledMinHours, setUnscheduledMinHours] = useLocalStorage<string>(
    'productionCalendarUnscheduledMinHours',
    ''
  )
  const [unscheduledMaxHours, setUnscheduledMaxHours] = useLocalStorage<string>(
    'productionCalendarUnscheduledMaxHours',
    ''
  )

  const unscheduledQuery = handlePipelineQuery({
    pipeline: 'production-pipeline',
    filters: [
      ...(unscheduledSelectedProjectTypes.length
        ? [
            {
              id: 'quoteType',
              type: 'in',
              value: unscheduledSelectedProjectTypes
            } as ValueFilter
          ]
        : []),
      ...(unscheduledSelectedProjectManagers.length
        ? [
            {
              id: 'owner.id',
              type: 'in',
              value: unscheduledSelectedProjectManagers
            } as ValueFilter
          ]
        : []),
      ...(unscheduledSelectedStages.length
        ? [
            {
              id: 'stage.value',
              type: 'in',
              value: unscheduledSelectedStages
            } as ValueFilter
          ]
        : []),
      ...(unscheduledTags.length
        ? [
            {
              id: 'tags',
              type: 'in',
              value: unscheduledTags
            } as ValueFilter
          ]
        : []),
      ...(unscheduledMinPrice
        ? [
            {
              id: 'value',
              type: 'greaterThan',
              value: parseFloat(unscheduledMinPrice)
            } as ValueFilter
          ]
        : []),
      ...(unscheduledMaxPrice
        ? [
            {
              id: 'value',
              type: 'lessThan',
              value: parseFloat(unscheduledMaxPrice)
            } as ValueFilter
          ]
        : []),
      ...(unscheduledMinHours
        ? [
            {
              id: 'hours',
              type: 'range',
              value: [parseFloat(unscheduledMinHours), undefined]
            } as ValueFilter
          ]
        : []),
      ...(unscheduledMaxHours
        ? [
            {
              id: 'hours',
              type: 'range',
              value: [undefined, parseFloat(unscheduledMaxHours)]
            } as ValueFilter
          ]
        : [])
    ],
    query: null
  })
  const [unscheduledBookmark, setUnscheduledBookmark] = useState<string | null>(null)
  const {
    data: unscheduledJobsData,
    fetchMore,
    refetch
  } = useUnsheduledJobsQuery({
    variables: { query: unscheduledQuery, limit: 8 },
    fetchPolicy: 'network-only'
  })

  useEffect(() => {
    if (!unscheduledJobsData) return
    const transformedEvents = mapActivitiesToCalendarEvents(
      unscheduledJobsData.unscheduledJobs.activities as EventActivity[],
      selectedUsers,
      true
    )
    // Filter out any events already on the timeline to prevent a stale server
    // response from restoring a job that was just scheduled locally.
    const scheduledIds = new Set(eventsRef.current.map((e) => e.id))
    const filteredEvents = transformedEvents.filter((e) => !scheduledIds.has(e.id))
    const filteredOutCount = transformedEvents.length - filteredEvents.length
    setUnscheduledBookmark(unscheduledJobsData.unscheduledJobs.bookmark)
    setTotalUnscheduledEvents(unscheduledJobsData.unscheduledJobs.total_rows - filteredOutCount)
    setUnscheduledEvents(filteredEvents)
  }, [unscheduledJobsData])

  useEffect(() => {
    unscheduledEventsRef.current = unscheduledEvents
  }, [unscheduledEvents])

  useEffect(() => {
    eventsRef.current = events
  }, [events])

  useSearchUsersQuery({
    variables: {
      query: usersQuery,
      includeInactive: true
    },
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => {
      const users = data?.searchUsers?.rows ?? []
      setSelectedUsers(users)
      setLoading(false)
    }
  })

  // Wrap fetchMore to match the expected signature
  const fetchMoreUnscheduledEvents = () => {
    return fetchMore({
      variables: {
        bookmark: unscheduledBookmark,
        limit: 8,
        query: unscheduledQuery
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        // Default Apollo behavior: merge results, or just return fetchMoreResult
        return fetchMoreResult
          ? {
              ...fetchMoreResult,
              unscheduledJobs: {
                ...fetchMoreResult.unscheduledJobs,
                activities: [
                  ...(prev.unscheduledJobs.activities as EventActivity[]),
                  ...(fetchMoreResult.unscheduledJobs.activities as EventActivity[])
                ]
              }
            }
          : prev
      }
    })
  }

  const updateEvent = async (updatedEvent: CalendarEvent) => {
    const isNowScheduled = !!updatedEvent.start
    const wasUnscheduled = unscheduledEventsRef.current.some((e) => e.id === updatedEvent.id)

    // Always filter the event from both arrays, then add to the correct one.
    // This is idempotent: calling twice for the same event won't create duplicates.
    setEvents((prev) => {
      const without = prev.filter((e) => e.id !== updatedEvent.id)
      return isNowScheduled ? [...without, updatedEvent] : without
    })

    setUnscheduledEvents((prev) => {
      const without = prev.filter((e) => e.id !== updatedEvent.id)
      return isNowScheduled ? without : [...without, updatedEvent]
    })

    // Adjust total count based on transition direction
    if (wasUnscheduled && isNowScheduled) {
      setTotalUnscheduledEvents((t) => t - 1)
    } else if (!wasUnscheduled && !isNowScheduled) {
      setTotalUnscheduledEvents((t) => t + 1)
    }

    // Eagerly update refs so rapid sequential calls and incoming server data see the correct state
    if (isNowScheduled) {
      unscheduledEventsRef.current = unscheduledEventsRef.current.filter((e) => e.id !== updatedEvent.id)
      eventsRef.current = [...eventsRef.current.filter((e) => e.id !== updatedEvent.id), updatedEvent]
    } else {
      unscheduledEventsRef.current = [
        ...unscheduledEventsRef.current.filter((e) => e.id !== updatedEvent.id),
        updatedEvent
      ]
      eventsRef.current = eventsRef.current.filter((e) => e.id !== updatedEvent.id)
    }

    await saveEvent({
      variables: {
        event: updatedEvent
      }
    })
  }

  const deleteEvent = useCallback(
    async (event: CalendarEvent) => {
      setEvents((prevEvents) => prevEvents.filter((prevEvent) => prevEvent.id !== event.id))
      await saveEvent({
        variables: {
          event: {
            ...event,
            canceled: true
          }
        }
      })
      refetch()
    },
    [refetch, saveEvent]
  )

  const filteredScheduledEvents = filterScheduledEvents({
    events,
    crews,
    filters: {
      selectedProjectTypes: scheduledSelectedProjectTypes,
      selectedSalesReps: scheduledSelectedSalesReps,
      selectedProjectManagers:
        view === 'agenda' ? scheduledSelectedProjectManagers : selectedUsers.map((u) => removeAuth0Prefix(u.user_id)),
      minPrice: scheduledMinPrice,
      maxPrice: scheduledMaxPrice
    }
  })

  return (
    <ProductionCalendarContext.Provider
      value={{
        date,
        setDate,
        targetDate,
        setTargetDate,
        startDate: visibleStartDate,
        endDate: visibleEndDate,
        setVisibleWindow,
        zoomLevel,
        setZoomLevel,
        monthColumns,
        setMonthColumns,
        quarterColumns,
        setQuarterColumns,
        view,
        setView,
        tab,
        loading: loading || loadingJobs,
        events: filteredScheduledEvents,
        setEvents,
        visibleEvents: filteredScheduledEvents.filter((event) => {
          // Only show events that are within the visible window
          return event.start && event.end && event.start < visibleEndDate && event.end > visibleStartDate
        }),
        unscheduledEvents,
        totalUnscheduledEvents,
        fetchMoreUnscheduledEvents: fetchMore ? fetchMoreUnscheduledEvents : null,
        scheduledFilters: {
          scheduledSelectedProjectTypes: scheduledSelectedProjectTypes || null,
          setScheduledSelectedProjectTypes: (types: string[] | null) => setScheduledSelectedProjectTypes(types || []),
          scheduledSelectedProjectManagers,
          setScheduledSelectedProjectManagers: (managers: string[]) => setScheduledSelectedProjectManagers(managers),
          scheduledSelectedSalesReps: scheduledSelectedSalesReps || [],
          setScheduledSelectedSalesReps: (salesReps: string[]) => setScheduledSelectedSalesReps(salesReps),
          scheduledMinPrice: scheduledMinPrice ? Number(scheduledMinPrice) : null,
          scheduledMaxPrice: scheduledMaxPrice ? Number(scheduledMaxPrice) : null,
          setScheduledMinPrice: (price: string) => setScheduledMinPrice(price ? String(price) : ''),
          setScheduledMaxPrice: (price: string) => setScheduledMaxPrice(price ? String(price) : '')
        },
        unscheduledFilters: {
          unscheduledSelectedProjectTypes: unscheduledSelectedProjectTypes || null,
          setUnscheduledSelectedProjectTypes: (types: string[] | null) =>
            setUnscheduledSelectedProjectTypes(types || []),
          unscheduledSelectedProjectManagers,
          setUnscheduledSelectedProjectManagers: (managers: string[]) =>
            setUnscheduledSelectedProjectManagers(managers),
          unscheduledMinPrice: unscheduledMinPrice ? Number(unscheduledMinPrice) : null,
          unscheduledMaxPrice: unscheduledMaxPrice ? Number(unscheduledMaxPrice) : null,
          setUnscheduledMinPrice: (price: string) => setUnscheduledMinPrice(price ? String(price) : ''),
          setUnscheduledMaxPrice: (price: string) => setUnscheduledMaxPrice(price ? String(price) : ''),
          unscheduledMinHours: unscheduledMinHours ? Number(unscheduledMinHours) : null,
          unscheduledMaxHours: unscheduledMaxHours ? Number(unscheduledMaxHours) : null,
          setUnscheduledMinHours: (hours: string) => setUnscheduledMinHours(hours ? String(hours) : ''),
          setUnscheduledMaxHours: (hours: string) => setUnscheduledMaxHours(hours ? String(hours) : ''),
          unscheduledSelectedStages: unscheduledSelectedStages || [],
          setUnscheduledSelectedStages: (stages: string[]) => setUnscheduledSelectedStages(stages),
          unscheduledTags: unscheduledTags || [],
          setUnscheduledTags: (tags: string[]) => setUnscheduledTags(tags)
        },
        selectedUsers,
        setSelectedUsers,
        updateEvent,
        deleteEvent
      }}
    >
      {children}
    </ProductionCalendarContext.Provider>
  )
}

export const useProductionCalendar = (): ProductionCalendarContextValue => {
  const context = useContext(ProductionCalendarContext)
  if (!context) {
    throw new Error('useProductionCalendar must be used within a ProductionCalendarProvider')
  }
  return context
}
